import React from 'react';
import { Chart, FileTypes } from '@usacsmideo/types';
import { Uploader } from '../Uploader';
import { useUpdateChartSection } from '../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../hooks/useInitChartSection';

/**
 * PoaUpload — reusable Power-of-Attorney document affordance.
 *
 * Authored by CU-86aha36kx; imported by CU-86aha0x95 (onboarding). It renders the
 * PoA-document upload with an "upload now vs. email me later" radio, against the
 * frozen OTHER_DOCUMENTS contract (types @usacsmideo/types >= 1.15.0):
 *
 *   powerOfAttorneyURI            string        (client; existing Uploader)
 *   powerOfAttorneyUploadTiming   'now'|'later' (client)
 *   powerOfAttorneyEmailRequested boolean       (client — true to request, false to cancel)
 *   powerOfAttorneyEmailSentAt    TimestampLike (BACKEND ONLY — read-only here)
 *
 * Render precedence (deployed sendPoaUploadEmail.ts contract):
 *   1. URI on file        → "on file" (and clear any stale EmailRequested)
 *   2. EmailSentAt present → "email sent" (latch; never re-sends)
 *   3. EmailRequested      → "we'll email you" (pending; no awaited confirmation)
 *   4. otherwise           → the now/later radio
 *   Both 2 and 3 offer "Actually, I'll upload it now", which writes timing 'now'
 *   AND EmailRequested false (cancel) so the component reaches 4 with the Uploader.
 *
 * The backend keys solely off EmailRequested===true && !EmailSentAt, fires on any
 * write to OTHER_DOCUMENTS, and is the sole writer of EmailSentAt. So the client
 * needs no re-trigger guard — just set EmailRequested. No self-service resend.
 */
export function PoaUpload({
  patientId,
  chart,
  bucket = 'patient',
}: {
  patientId: string;
  chart: Chart;
  /**
   * Storage bucket for the Uploader. Defaults to 'patient'. Caller passes the
   * value matching its existing screen behavior to avoid changing where PoA docs
   * land. NOTE: pre-existing inconsistency in the codebase — the Files page used
   * 'patient', the HCA screen used 'public'. Flagged for Shaun; not resolved here.
   */
  bucket?: 'patient' | 'public' | 'video';
}) {
  const { mutate, mutateAsync } = useUpdateChartSection();
  const otherDocs = chart?.OTHER_DOCUMENTS;
  const uri = otherDocs?.powerOfAttorneyURI;
  const hasFile = !!uri && uri.startsWith('https://');
  const emailSent = !!otherDocs?.powerOfAttorneyEmailSentAt;
  const emailRequested = otherDocs?.powerOfAttorneyEmailRequested === true;
  const timing = otherDocs?.powerOfAttorneyUploadTiming;

  // Effective timing is DERIVED from the persisted chart value, not held in local
  // state — so it survives a chart refetch / screen revisit and never drifts from
  // what's saved (audit from CU-86aha0x95). `override` is only an optimistic, in-
  // session echo for instant feedback before the write round-trips; the persisted
  // `timing` always wins once it arrives/refetches.
  const [override, setOverride] = React.useState<'now' | 'later' | undefined>(undefined);
  const effectiveTiming: 'now' | 'later' | 'unset' =
    timing === 'now' || timing === 'later' ? timing : override ?? 'unset';

  const writeOtherDocs = (data: Record<string, unknown>) => {
    mutate({ patientId, docData: { OTHER_DOCUMENTS: data } });
  };

  // If the file now exists but a stale "email later" request is still pending,
  // clear it so the backend no-ops (rule 3). EmailSentAt is backend-only — never written here.
  // Once per failure, not once per snapshot (CU-17tm466jq2e): a rejected clear is applied locally
  // (emailRequested → false) and rolled back (→ true), which re-ran a plain effect without limit.
  // Silent on failure, as before: useUpdateChartSection already reports it to Sentry.
  useInitChartSection({
    needed: hasFile && emailRequested,
    write: () => mutateAsync({ patientId, docData: { OTHER_DOCUMENTS: { powerOfAttorneyEmailRequested: false } } }),
    onError: () => {},
  });

  const chooseNow = () => {
    setOverride('now');
    // Clear the email request too (CU-86akqgabf). Leaving it true kept the
    // component on the "we'll email you" / "emailed you" branches — the
    // Uploader never appeared — and sendPoaUploadEmail fires on ANY
    // OTHER_DOCUMENTS write while EmailRequested === true && !EmailSentAt,
    // so this very write could send the "upload later" email.
    writeOtherDocs({
      powerOfAttorneyUploadTiming: 'now',
      powerOfAttorneyEmailRequested: false,
    });
  };

  const chooseLater = () => {
    setOverride('later');
    writeOtherDocs({
      powerOfAttorneyUploadTiming: 'later',
      powerOfAttorneyEmailRequested: true,
    });
  };

  // 1. File already on record.
  if (hasFile) {
    return (
      <div className="flex flex-col items-center">
        <a
          className="font-medium text-blue-600 hover:underline dark:text-blue-500"
          href={uri}
          target="_blank"
          rel="noreferrer"
        >
          <p className="text-xl">View current medical durable power of attorney</p>
        </a>
        <p className="mt-2 text-sm text-gray-600">
          A document is on record. To replace it or upload a new one, please contact support.
        </p>
      </div>
    );
  }

  // 2. Email already sent (backend latch). Still offer an in-portal upload — the
  //    emailed link just becomes redundant (CU-86akqgabf); without this, a
  //    patient who chose "later" had no way to upload here once it was sent.
  if (emailRequested && emailSent) {
    return (
      <div className="flex flex-col items-center">
        <p className="text-xl">
          We&apos;ve emailed you a link to upload your Power of Attorney document later.
        </p>
        <button
          type="button"
          className="mt-2 text-sm text-blue-600 hover:underline"
          onClick={chooseNow}
        >
          Actually, I&apos;ll upload it now
        </button>
      </div>
    );
  }

  // 3. "Later" chosen, email not yet sent → pending (no awaited confirmation).
  if (emailRequested || effectiveTiming === 'later') {
    return (
      <div className="flex flex-col items-center">
        <p className="text-xl">
          No problem — we&apos;ll email you a link so you can upload your Power of Attorney
          document later.
        </p>
        <button
          type="button"
          className="mt-2 text-sm text-blue-600 hover:underline"
          onClick={chooseNow}
        >
          Actually, I&apos;ll upload it now
        </button>
      </div>
    );
  }

  // 4. Fresh radio: now vs. later.
  return (
    <div className="flex flex-col items-center">
      <h2 className="text-xl font-bold">Please provide a copy of the Healthcare Power of Attorney document</h2>
      <fieldset className="my-2 flex flex-col items-start">
        <legend className="mb-1 text-lg font-bold">
          Are you prepared to upload it now, or would you like us to email you a link to upload later?
        </legend>
        <label className="flex flex-row items-center text-lg">
          <input
            type="radio"
            name="poa-upload-timing"
            value="now"
            checked={effectiveTiming === 'now'}
            onChange={chooseNow}
            className="mr-2 h-5 w-5"
          />
          Upload now
        </label>
        <label className="flex flex-row items-center text-lg">
          <input
            type="radio"
            name="poa-upload-timing"
            value="later"
            // We only render this block when selection is 'now' | 'unset' (the
            // 'later' path returns earlier as the pending state), so this radio
            // is never checked here.
            checked={false}
            onChange={chooseLater}
            className="mr-2 h-5 w-5"
          />
          Email me a link to upload later
        </label>
      </fieldset>

      {effectiveTiming === 'now' && (
        <Uploader
          docToUpdate="OTHER_DOCUMENTS"
          bucket={bucket}
          type={FileTypes.powerOfAttorney}
          title="Upload Medical Durable Power of Attorney"
          elementId="powerOfAttorney-file-upload"
        />
      )}
    </div>
  );
}

export default PoaUpload;
